// Product page video player for the vr-product-video snippet.
// Plays the full desktop or mobile file muted and looping while on screen, pauses
// off screen. The control bar seeks, pauses, unmutes and goes full screen.
// Reduced motion: starts paused on the poster until the shopper presses play.
// Captions (G1): CC appears only once the .vtt loads. Off by default, the choice is remembered.
// While playing, the control bar fades out after a few idle seconds and comes back on a mouse move, a tap
// or keyboard focus. On touch screens a tap on the video shows or hides the controls, and the middle of the
// video gets a big pause/play button with 10-second skips (they show and hide with the bar).
// Sound: on desktop browsers the first unmute routes the audio through Web Audio and later mute
// toggles only move a gain, so the video element's own muted flag changes once. Toggling it on a
// playing video makes Chrome drop a few frames (a visible hitch). iPhone keeps the plain toggle.
if (!customElements.get('vr-product-video')) {
  const MOBILE = window.matchMedia('(max-width: 767px)');
  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');
  const CC_KEY = 'vr-captions';
  const IDLE_MS = 2500;
  const clock = (s) => {
    s = Math.max(0, Math.floor(s || 0));
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
  };

  customElements.define('vr-product-video', class extends HTMLElement {
    connectedCallback() {
      if (!this.ready) this.setup();
      MOBILE.addEventListener('change', this.onMedia);
      // Load a little before the player scrolls in; play only while it is actually on screen.
      this.nearObserver = new IntersectionObserver(([entry]) => {
        this.near = entry.isIntersecting;
        if (!this.near) return;
        this.loadSource(false);
        this.loadCaptions(); // DD5: fetched with the video, not on page load
      }, { rootMargin: '200px 0px' });
      this.observer = new IntersectionObserver(([entry]) => {
        this.visible = entry.isIntersecting;
        this.update();
      });
      this.nearObserver.observe(this);
      this.observer.observe(this);
    }

    disconnectedCallback() {
      this.nearObserver?.disconnect();
      this.observer?.disconnect();
      MOBILE.removeEventListener('change', this.onMedia);
      this.visible = false;
      this.video.pause();
    }

    // Listeners on the player's own elements, added once even if the theme moves the player.
    setup() {
      this.ready = true;
      this.video = this.querySelector('video');
      this.track = this.querySelector('.vr-product-video__track');
      this.fill = this.querySelector('.vr-product-video__fill');
      this.time = this.querySelector('.vr-product-video__time');
      this.playButton = this.querySelector('[data-action="play"]');
      this.bigButton = this.querySelector('[data-action="play-center"]');
      this.muteButton = this.querySelector('[data-action="mute"]');
      this.ccButton = this.querySelector('[data-action="captions"]');
      this.captionsBox = this.querySelector('.vr-product-video__captions');
      this.userPaused = REDUCED.matches;
      this.sync();

      // The action follows what the shopper sees: something else (the theme, iOS full screen) may have paused it.
      const toggle = () => this.setPaused(!(this.userPaused || this.video.paused));
      this.playButton.addEventListener('click', toggle);
      this.bigButton?.addEventListener('click', toggle);
      this.querySelector('[data-action="back"]')?.addEventListener('click', () => this.seek(this.video.currentTime - 10));
      this.querySelector('[data-action="forward"]')?.addEventListener('click', () => this.seek(this.video.currentTime + 10));
      this.video.addEventListener('pointerdown', (e) => { this.pointer = e.pointerType; });
      this.video.addEventListener('click', (e) => {
        // On a phone a tap shows or hides the controls; pausing is the big button in the middle.
        if (this.pointer !== 'mouse') {
          e.stopPropagation(); // the frame's own click would show the bar again
          return this.classList.contains('is-idle') ? this.wake() : this.rest();
        }
        toggle();
      });
      this.addEventListener('pointermove', (e) => { if (e.pointerType === 'mouse') this.wake(); });
      this.addEventListener('pointerleave', (e) => { if (e.pointerType === 'mouse') this.rest(); });
      this.addEventListener('click', () => this.wake());
      this.addEventListener('focusin', () => this.wake());
      this.addEventListener('keydown', () => this.wake());
      this.muteButton.addEventListener('click', () => this.setMuted(!this.isMuted()));
      this.querySelector('[data-action="fullscreen"]').addEventListener('click', () => this.fullscreen());
      this.track.addEventListener('click', (e) => {
        const r = this.track.getBoundingClientRect();
        this.seek(((e.clientX - r.left) / r.width) * (this.video.duration || 0));
      });
      this.track.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
          e.preventDefault();
          this.seek(this.video.currentTime + (e.key === 'ArrowRight' ? 5 : -5));
        }
      });
      this.video.addEventListener('timeupdate', () => this.progress());
      this.video.addEventListener('loadedmetadata', () => this.progress());
      this.video.addEventListener('play', () => this.sync());
      this.video.addEventListener('pause', () => this.sync());
      this.video.addEventListener('error', () => this.onError());
      this.onMedia = () => this.loadSource(true);
    }

    // Shopify serves .vtt as octet-stream, which browsers refuse in <track>, so the text is re-typed as a text/vtt Blob.
    loadCaptions() {
      if (this.captionsRequested || !this.video.dataset.captionsSrc || !this.ccButton) return;
      this.captionsRequested = true;
      fetch(this.video.dataset.captionsSrc)
        .then((r) => { if (!r.ok) throw new Error(`captions ${r.status}`); return r.text(); })
        .then((text) => {
          const el = document.createElement('track');
          el.kind = 'captions';
          el.srclang = 'en';
          el.label = 'English';
          el.src = URL.createObjectURL(new Blob([text], { type: 'text/vtt' }));
          el.addEventListener('load', () => {
            this.ccButton.hidden = false;
            let saved = false;
            try { saved = localStorage.getItem(CC_KEY) === '1'; } catch (e) { /* storage blocked */ }
            this.setCaptions(saved, false);
          }, { once: true });
          el.addEventListener('error', () => this.captionsFailed(), { once: true });
          this.video.appendChild(el);
          this.textTrack = el.track;
          this.textTrack.mode = 'hidden'; // the player draws cues itself, above the control bar
          this.textTrack.addEventListener('cuechange', () => this.drawCues());
          this.ccButton.addEventListener('click', () => this.setCaptions(!this.ccOn, true));
          // iPhone full screen hides the page, so the native caption renderer takes over there.
          this.video.addEventListener('webkitbeginfullscreen', () => { if (this.ccOn) this.textTrack.mode = 'showing'; });
          this.video.addEventListener('webkitendfullscreen', () => { this.textTrack.mode = 'hidden'; });
        })
        .catch(() => this.captionsFailed());
    }

    setCaptions(on, remember) {
      this.ccOn = on;
      this.classList.toggle('is-cc-on', on);
      this.ccButton.setAttribute('aria-pressed', String(on));
      if (remember) {
        try { localStorage.setItem(CC_KEY, on ? '1' : '0'); } catch (e) { /* storage blocked */ }
      }
      this.drawCues();
    }

    // E7: every cue line is shown in full. Only <i> and <b> survive, other cue markup keeps its text.
    drawCues() {
      const box = this.captionsBox;
      box.replaceChildren();
      if (!this.ccOn || !this.textTrack?.activeCues) return;
      const copy = (from, to) => {
        from.childNodes.forEach((n) => {
          if (n.nodeType === Node.TEXT_NODE) return to.append(n.textContent);
          if (n.nodeType !== Node.ELEMENT_NODE) return;
          const tag = n.tagName.toLowerCase();
          if (tag === 'i' || tag === 'b') {
            const keep = document.createElement(tag);
            copy(n, keep);
            to.append(keep);
          } else {
            copy(n, to);
          }
        });
      };
      Array.from(this.textTrack.activeCues).forEach((cue) => {
        const line = document.createElement('span');
        copy(cue.getCueAsHTML(), line);
        box.append(line);
      });
    }

    // DD9: a missing or broken file removes CC instead of showing a button that does nothing.
    captionsFailed() {
      this.ccOn = false;
      this.textTrack = null;
      this.classList.remove('is-cc-on');
      if (this.ccButton) this.ccButton.hidden = true;
      this.captionsBox?.replaceChildren();
    }

    // Picks the file for the screen size. On a size change it keeps the spot in the video.
    loadSource(force) {
      const v = this.video;
      const src = MOBILE.matches ? v.dataset.mobileSrc : v.dataset.desktopSrc;
      if (!src || v.dataset.loaded === src || (!force && v.dataset.loaded)) return;
      if (force ? !v.dataset.loaded : !(this.near || this.visible)) return; // a size change only swaps a file already playing
      const at = v.dataset.loaded ? v.currentTime : 0;
      v.dataset.loaded = src;
      v.src = src;
      if (at) v.addEventListener('loadedmetadata', () => { v.currentTime = at; }, { once: true });
      v.load();
      this.update();
    }

    setPaused(paused) {
      this.userPaused = paused;
      if (!paused) this.loadSource(false);
      this.update();
    }

    update() {
      const v = this.video;
      if (this.visible && !this.userPaused && v.dataset.loaded) {
        // A blocked autoplay leaves the video paused; the bar then shows the play icon.
        // AbortError only means a new file replaced the one loading, so it is ignored.
        v.play().catch((e) => {
          if (e.name === 'AbortError') return;
          this.userPaused = true;
          this.sync();
        });
      } else {
        v.pause();
      }
      this.sync();
    }

    seek(t) {
      const v = this.video;
      if (!v.duration) return;
      v.currentTime = Math.min(Math.max(t, 0), v.duration);
      this.progress();
    }

    fullscreen() {
      if (document.fullscreenElement) return document.exitFullscreen();
      if (this.requestFullscreen) return this.requestFullscreen().catch(() => {});
      this.video.webkitEnterFullscreen?.(); // iPhone only allows the video itself to go full screen
    }

    progress() {
      const v = this.video;
      const pct = v.duration ? (v.currentTime / v.duration) * 100 : 0;
      this.fill.style.width = `${pct}%`;
      this.track.setAttribute('aria-valuenow', String(Math.round(pct)));
      this.track.setAttribute('aria-valuetext', `${clock(v.currentTime)} of ${clock(v.duration)}`);
      this.time.textContent = `${clock(v.currentTime)} / ${clock(v.duration)}`;
    }

    isMuted() {
      return this.gain ? this.gain.gain.value === 0 : this.video.muted;
    }

    // Web Audio needs a cross-origin-allowed file, a running context (a user gesture) and no iPhone native player.
    canRoute() {
      return !this.noWebAudio && typeof AudioContext !== 'undefined' && document.fullscreenEnabled
        && this.video.crossOrigin !== null;
    }

    setMuted(muted) {
      if (this.gain) {
        this.gain.gain.value = muted ? 0 : 1;
        this.sync();
        return;
      }
      if (!muted && this.canRoute()) {
        const ctx = new AudioContext();
        const attach = () => {
          if (ctx.state !== 'running') return false;
          const gain = ctx.createGain();
          ctx.createMediaElementSource(this.video).connect(gain).connect(ctx.destination);
          this.gain = gain;
          this.video.muted = false; // the one and only change to the element's own flag
          this.sync();
          return true;
        };
        if (attach()) return;
        // Still suspended inside the gesture: resume, then attach, or give up on routing for good.
        const plain = () => { this.noWebAudio = true; ctx.close().catch(() => {}); this.video.muted = false; this.sync(); };
        ctx.resume().then(() => { if (!attach()) plain(); }, plain);
        return;
      }
      this.video.muted = muted;
      this.sync();
    }

    // Shows the control bar, then hides it again after IDLE_MS while the video plays.
    wake() {
      this.classList.remove('is-idle');
      clearTimeout(this.idleTimer);
      if (this.userPaused || this.video.paused) return;
      this.idleTimer = setTimeout(() => this.rest(), IDLE_MS);
    }

    // Hides the control bar, unless the video is paused or a control has keyboard focus.
    rest() {
      clearTimeout(this.idleTimer);
      if (this.userPaused || this.video.paused || this.querySelector(':focus-visible')) return;
      this.classList.add('is-idle');
    }

    sync() {
      const paused = this.userPaused || this.video.paused;
      const label = (el, text) => { el.setAttribute('aria-label', text); el.title = text; };
      this.classList.toggle('is-paused', paused);
      const muted = this.isMuted();
      this.classList.toggle('is-unmuted', !muted);
      label(this.playButton, paused ? 'Play' : 'Pause');
      if (this.bigButton) label(this.bigButton, paused ? 'Play' : 'Pause');
      label(this.muteButton, muted ? 'Unmute' : 'Mute');
      if (paused !== this.wasPaused) this.wake(); // playing starts the idle countdown; pausing shows the bar
      this.wasPaused = paused;
    }

    // E6: a broken file shows the poster instead of a black box.
    // O3: and tells analytics (P6) which video failed.
    onError() {
      const v = this.video;
      // A file served without cross-origin headers won't load with crossorigin set: load it plainly instead
      // (then sound can't go through Web Audio, so the mute toggle stays on the element).
      if (v.crossOrigin !== null && !this.corsRetried && v.dataset.loaded) {
        this.corsRetried = true;
        this.noWebAudio = true;
        v.removeAttribute('crossorigin');
        v.src = v.dataset.loaded;
        v.load();
        this.update();
        return;
      }
      window.Shopify?.analytics?.publish?.('vr_video_error', {
        player: 'product', video: v.getAttribute('aria-label'), src: v.currentSrc || v.src,
      });
      this.userPaused = true;
      delete v.dataset.loaded;
      v.removeAttribute('src');
      v.load();
      this.sync();
    }
  });
}
